import { Button, buttonVariants, HStack, VStack } from 'ui_2';
import { BiChevronLeft } from 'react-icons/bi';
import { Link } from '@tanstack/react-router';
import { ReactNode, useEffect, useState } from 'react';
import { useBreakpoints } from '@puzzlehq/frontend-utils';
import {
  AnalyticsEvents,
  useAnalytics,
  useAnalyticsDefaultValues,
} from '@src/providers/analytics';
import { Sponsor } from 'jigsaw';
import { router } from '@src/providers/router';
import { Route as index_route } from '@src/routes/index';
import { useViewTransition } from '@src/hooks/useViewTransition';

type Props = {
  sponsor: Sponsor;
  smallRow: ReactNode;
  giveawayId?: string;
  service?: string;
  navigate: () => void;
};

export const AdPage = ({
  sponsor,
  smallRow,
  giveawayId,
  service,
  navigate,
}: Props) => {
  const { slideRightFullPage } = useViewTransition();
  const hasHistory = router.history.length > 1;

  const [countdown, setCountdown] = useState(3);
  const [imgLoaded, setImgLoaded] = useState(false);

  const { isSmall } = useBreakpoints();
  const analytics = useAnalytics();
  const { address, network, partBalance } = useAnalyticsDefaultValues();

  useEffect(() => {
    const loadTimeout = setTimeout(() => {
      setImgLoaded(true);
    }, 5000);
    return () => clearTimeout(loadTimeout);
  }, []);

  useEffect(() => {
    if (imgLoaded) {
      const timer = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown > 0) {
            return Math.max(prevCountdown - 0.25, 0);
          } else {
            clearInterval(timer);
            return 0;
          }
        });
      }, 250);

      return () => clearInterval(timer);
    }
  }, [imgLoaded]);

  return (
    <div className='relative h-full w-full'>
      {/* Background Image */}
      <button
        className='absolute inset-0 z-10 w-full bg-cover bg-center object-cover sm:max-w-[800px] sm:self-center sm:justify-self-center sm:px-20'
        onClick={() => {
          analytics.track(AnalyticsEvents.AdSplashClicked, {
            sponsor: sponsor.name,
            address,
            network,
            partBalance,
            giveawayId,
            service,
          });
          window.open(sponsor.url, '_blank');
        }}
        disabled={!isSmall || !imgLoaded}
      >
        <img
          src={
            isSmall
              ? sponsor.splashPageImageDesktopURL
              : sponsor.splashPageBackgroundImageMobileURL
          }
          onLoad={() => {
            setImgLoaded(true);
            analytics.track(AnalyticsEvents.AdSplashViewed, {
              sponsor: sponsor.name,
              address,
              network,
              partBalance,
              giveawayId,
              service,
            });
          }}
          alt={`${sponsor.name} Ad`}
        />
      </button>

      {/* Content */}
      <div className='relative z-20 flex w-full flex-grow p-4'>
        <VStack className='z-20 h-fit w-full gap-4'>
          {hasHistory && (
            <Link
              to={index_route.to}
              className={buttonVariants({
                variant: 'icon-white',
                size: 'icon',
              })}
              viewTransition={slideRightFullPage}
            >
              <BiChevronLeft />
            </Link>
          )}
          <HStack className='w-full justify-center sm:max-w-[800px] sm:self-center sm:justify-self-center sm:px-16'>
            {smallRow}
          </HStack>
        </VStack>
      </div>

      {/* Bottom Button */}
      <HStack className='absolute bottom-8 left-0 z-20 w-full justify-center px-4 pb-safe'>
        <Button
          className='w-full max-w-[400px]'
          variant={countdown > 0 ? 'gray' : 'primary'}
          disabled={countdown > 0}
          onClick={navigate}
        >
          {countdown > 0
            ? `Continue in ${countdown.toFixed(0)}...`
            : 'Continue'}
        </Button>
      </HStack>
    </div>
  );
};
